import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { isPresent, isEmpty } from '@ember/utils'
import { trackedFunction } from 'reactiveweb/function'
import { truncate } from 'lodash'
import { FILTER_TYPES, METADATA_ATTRIBUTE_MAP, type FilterKey } from 'teacher-ui-client/reading-scales/utils'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import {
  serialize as serializeSelected,
  deserialize as deserializeSelected,
} from '@blakeelearning/data-tables/utils/selecting'
import { type IntlService, t } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type BookLibraryCollectionService from 'teacher-ui-client/services/book-library/collection'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'
import type { PrimaryFilter } from 'teacher-ui-client/reading-scales/utils'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type { SafeString } from '@ember/template'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import { LinkTo } from '@ember/routing'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import SelectableCell from 'teacher-ui-client/components/stable-table/basic-cells/selectable'
import ImageCell from 'teacher-ui-client/components/stable-table/basic-cells/image'
import BookDetailCell from 'teacher-ui-client/components/stable-table/basic-cells/detail-cell'
import { hash, array, fn } from '@ember/helper'
import { BookLibraryPrimaryFilterSelect } from 'teacher-ui-client/components/book-library/primary-filter-select'
import { BooksTableBooksTaskButton } from 'teacher-ui-client/components/book-library/collection/books-table/books-task-button'
import { on } from '@ember/modifier'
import { BookLibraryCollectionSelect } from 'teacher-ui-client/components/book-library/collection-select'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { BookLibraryConfirmBookChangesModal } from 'teacher-ui-client/components/book-library/confirm-book-changes-modal'
import { BookLibraryResourcesLink } from 'teacher-ui-client/components/book-library/resources-link'
import { or } from 'ember-truth-helpers'
import type { RowId } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import PopoverStandardDropdown from '@blakeelearning/popovers/components/popover/dropdowns/standard'
import { hasId } from 'teacher-ui-client/utils/type-guards'

/**
 * Displays one row for each book in a collection.
 */

interface Args {
  collection: BookLibraryCollection
  otherCollections?: BookLibraryCollection[]
  primaryFilters: PrimaryFilter[]
  isCollectionOwner?: boolean
}

interface Signature {
  Args: Args
}

interface Column {
  value?: string | SafeString
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SelectableCell | typeof SortableCell
  cellComponent?: typeof SelectableCell | typeof ImageCell | typeof BookDetailCell
  isRemoveBooksColumn?: boolean
  componentClassNames?: string
  cellComponentClassNames?: string
  isMoveBooksColumn?: boolean
  adjacentIcon?: boolean
  isResourcesColumn?: boolean
}

interface BookItem {
  id: string
  previewImg?: {
    width: number
    height: number
    src: string
    alt: string
  }
  title: string
  bookModel: BookLibraryBook
  classNames: string
  bookMetadata: BookMetadata
  bookDescription: string
  bookDescriptionTruncated: string
}

interface BookMetadata {
  lexileRange?: string
  dra?: string
  // eslint-disable-next-line customPlugin/prefer-american-color
  bookColour?: string
  fpAz?: string
  rrLevel?: number
  yearLevel?: string
  readingAge?: string
}

export class BookLibraryCollectionBooksTable extends Component<Signature> {
  @service intl!: IntlService

  @service dataTransformer!: DataTransformerService

  @service flashQueue!: FlashQueueService

  @service('book-library/collection') collectionService!: BookLibraryCollectionService

  @queryParam('subscription-type/book-library/collection/show') sort!: string

  @queryParam('subscription-type/book-library/collection/show') selected!: string

  @queryParam('subscription-type/book-library/collection/show') primaryFilterKey!: FilterKey

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  get selectedIds() {
    return deserializeSelected(this.selected, String)
  }

  set selectedIds(newSelectedIds) {
    this.selected = serializeSelected(newSelectedIds)
  }

  @action
  updateSorts({ sortKey }: SortObject) {
    const newSortConfig = updateSortColumns(this.sortingConfig, sortKey as string)
    this.sortingConfig = newSortConfig
  }

  @action
  updateSelected(ids: RowId[]) {
    this.selectedIds = [...ids.filter((id): id is string => typeof id === 'string')]
  }

  @action
  updatePrimaryFilter(primaryFilterKey: FilterKey) {
    this.primaryFilterKey = primaryFilterKey
  }

  get books() {
    return this.args.collection?.books?.filter(hasId) || []
  }

  get otherCollections() {
    return this.args.otherCollections || []
  }

  get selectableIds() {
    return this.books.map((item) => item.id)
  }

  get selectedBooks() {
    return this.books.filter((book) => this.selectedIds.includes(book.id))
  }

  get disableBookActions() {
    return this.collectionService.removeBooks.isRunning || this.collectionService.moveBooks.isRunning
  }

  get disableBulkBookActions() {
    return this.disableBookActions || this.selectedIds.length === 0
  }

  get primaryFilters() {
    return this.args.primaryFilters || []
  }

  get currentPrimaryFilter() {
    const primaryFilter = this.primaryFilters.find((filter) => filter.id === this.primaryFilterKey)
    return primaryFilter ?? this.primaryFilters[0]
  }

  get primaryFilterColumns(): Column[] {
    const { primaryFilters, currentPrimaryFilter } = this
    // Sorting by Blake Reading Level works for most primary filters - the ones that are Reading Scales. Unfortunately,
    // Reading Age is separate, it is not a Reading Scale so cannot use Blake Reading Level for sorting. For example,
    // two books may have the same Blake Reading Level of "19" but their Reading Age might be 5 and 7 respectively
    // (could be based on the topic of the book). If the sorting was based on Blake Reading Level in this case, the
    // positions of the sorted books would not be correct.
    const readingAgeAttr = `bookMetadata.${String(METADATA_ATTRIBUTE_MAP[FILTER_TYPES.READING_AGE])}`
    const blakeReadingLevelAttr = `bookMetadata.${String(METADATA_ATTRIBUTE_MAP[FILTER_TYPES.READING_LEVEL])}`
    const sortValuePath = currentPrimaryFilter?.id === FILTER_TYPES.READING_AGE ? readingAgeAttr : blakeReadingLevelAttr
    return [
      ...primaryFilters.map((primaryFilter) => {
        const isHidden = currentPrimaryFilter?.id !== primaryFilter?.id
        return {
          id: primaryFilter.id,
          valuePath: `bookMetadata.${String(METADATA_ATTRIBUTE_MAP[primaryFilter.id])}`,
          cellClassNames: 'text-center',
          isPrimaryFilterColumn: true,
          isHidden,
        }
      }),
      {
        valuePath: sortValuePath,
        classNames: 'w-px print:hidden',
        comparerKey: 'sortByNumber',
        component: SortableCell,
        cellClassNames: 'invisible print:hidden',
      },
    ]
  }

  get columns(): Column[] {
    const { intl, primaryFilterColumns } = this
    const { isCollectionOwner } = this.args
    const defaultColumn = {
      cellClassNames: 'py-3',
    }

    const selectColumns: Column[] = [
      {
        value: intl.t(`bookLibrary.components.collectionBooksTable.headers.selectAll`),
        component: SelectableCell,
        classNames: 'w-px print:hidden',
        componentClassNames: 'py-3 font-normal',
        cellClassNames: 'print:hidden',
        cellComponent: SelectableCell,
        cellComponentClassNames: 'px-3 py-12',
      },
    ]
    const actionColumns: Column[] = [
      {
        ...defaultColumn,
        isRemoveBooksColumn: true,
      },
      {
        ...defaultColumn,
        isMoveBooksColumn: true,
      },
    ]

    return [
      ...(isCollectionOwner ? selectColumns : []),
      {
        ...defaultColumn,
        valuePath: 'previewImg',
        classNames: 'print:hidden',
        cellComponent: ImageCell,
        cellClassNames: 'min-w-24 w-px h-32 py-3 print:hidden',
      },
      {
        ...defaultColumn,
        value: intl.t('bookLibrary.components.collectionBooksTable.headers.title'),
        valuePath: 'title',
        cellClassNames: 'font-bold text-left pl-3',
        comparerKey: 'sortByValue',
        component: SortableCell,
        componentClassNames: 'font-normal',
        adjacentIcon: true,
      },
      {
        ...defaultColumn,
        isResourcesColumn: true,
      },
      ...primaryFilterColumns,
      {
        ...defaultColumn,
        cellComponent: BookDetailCell,
      },
      ...(isCollectionOwner ? actionColumns : []),
    ]
  }

  get rows() {
    return this.transformedItems.value?.filter(hasId).map((book) => this.bookViewModel(book)) || []
  }

  bookViewModel(bookModel: BookLibraryBook & { id: string }): BookItem {
    // Specify desired image width, to avoid it taking up all available width, & keep aspect ratio
    const adjustedImageWidth = 75
    let previewImg = undefined
    if (bookModel.bookPreviewImg) {
      previewImg = {
        src: String(bookModel.bookPreviewImg.url),
        width: adjustedImageWidth,
        height: (adjustedImageWidth / bookModel.bookPreviewImg.width) * bookModel.bookPreviewImg.height,
        alt: this.intl.t('bookLibrary.components.collectionBooksTable.previewImgAlt', { title: bookModel.title }),
      }
    }

    const bookMetadata: BookMetadata = Object.values(METADATA_ATTRIBUTE_MAP).reduce((props, attributeKey) => {
      return { ...props, [attributeKey]: bookModel.bookMetadata?.[attributeKey] }
    }, {})

    return {
      id: bookModel.id,
      previewImg,
      title: bookModel.title,
      bookModel,
      classNames: `border-b border-dusty-black-100 ${bookModel.exclude ? 'opacity-30' : ''}`,
      bookMetadata,
      bookDescription: bookModel.description,
      bookDescriptionTruncated: truncate(bookModel.description ?? '', { length: 150, separator: ' ' }),
    }
  }

  get isTransforming() {
    return this.transformedItems.isPending
  }

  get pipelineDescriptors(): Nullable<PipelineDescriptor>[] {
    return [this.sortingDescriptor]
  }

  get sortingDescriptor() {
    const { sortingConfig, columns: tableColumns } = this
    if (!sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(tableColumns)
    const sortingConfigWithComparerKeys = injectComparerKeys(sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort' as const, config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  transformedItems = trackedFunction(this, async (): Promise<BookLibraryBook[]> => {
    const presentDescriptors = this.pipelineDescriptors.filter(isPresent)
    const transformedData = await this.dataTransformer.buildAndTransform<BookLibraryBook, BookLibraryBook>(
      this.args.collection.books?.slice() ?? [],
      presentDescriptors,
    )
    return transformedData.items
  })

  get noRows() {
    return this.books.length === 0
  }

  get hasNoData() {
    return this.transformedItems.isFinished && this.noRows
  }

  get showEmptyMessage() {
    return this.hasNoData
  }

  @tracked confirmChangeBooks?: () => void = undefined

  @tracked clearChangeBooks = () => {
    this.confirmChangeBooks = undefined
  }

  @action
  removeBooks(books: BookLibraryBook[]) {
    this.confirmChangeBooks = () => void this.onRemoveBooks(books)
  }

  @action
  moveBooks(books: BookLibraryBook[], toCollection: BookLibraryCollection) {
    this.confirmChangeBooks = () => void this.onMoveBooks(books, toCollection)
  }

  // move books to another collection

  @action
  unselectIds(bookIds: string[]) {
    this.updateSelected(this.selectedIds.filter((id) => !bookIds.includes(id)))
  }

  @action
  async onMoveBooks(books: BookLibraryBook[], toCollection: BookLibraryCollection) {
    if (isEmpty(books) || !this.args.collection) return
    const { ok } = await this.collectionService.moveBooks.perform(this.args.collection, toCollection, books)
    if (ok) {
      const successMsg = this.intl.t('bookLibrary.collections.actions.moveTo.success', {
        htmlSafe: true,
        count: books.length,
        collectionName: toCollection.name,
        title: books[0].title,
      })
      this.flashQueue.addSuccess({ subtitle: successMsg })
      this.clearChangeBooks()
      this.unselectIds(books.filter(hasId).map((book) => book.id))
    } else {
      const failMsg = this.intl.t('bookLibrary.collections.actions.moveTo.fail', {
        count: books.length,
        collectionName: toCollection.name,
        title: books[0].title,
      })
      this.flashQueue.addFail({ subtitle: failMsg })
    }
  }

  // remove books from collection

  @action
  async onRemoveBooks(books: BookLibraryBook[]) {
    if (isEmpty(books) || !this.args.collection) return
    const { ok } = await this.collectionService.removeBooks.perform(this.args.collection, books)
    if (ok) {
      const successMsg = this.intl.t('bookLibrary.collections.actions.removeFrom.success', {
        htmlSafe: true,
        count: books.length,
        collectionName: this.args.collection.name,
        title: books[0].title,
      })
      this.flashQueue.addSuccess({ subtitle: successMsg })
      this.clearChangeBooks()
      this.unselectIds(books.filter(hasId).map((book) => book.id))
    } else {
      const failMsg = this.intl.t('bookLibrary.collections.actions.removeFrom.fail', {
        count: books.length,
        collectionName: this.args.collection.name,
        title: books[0].title,
      })
      this.flashQueue.addFail({ subtitle: failMsg })
    }
  }

  <template>
    <div>
      {{#if this.showEmptyMessage}}
        <div class="flex flex-col items-center justify-center">
          <p class="mt-32 mb-6 text-xl font-semibold italic">{{t
              "bookLibrary.components.collectionBooksTable.empty"
            }}</p>
          <p class="mb-6 text-xl font-semibold italic">
            <LinkTo @route="subscription-type.book-library.search">
              {{t "bookLibrary.components.collectionBooksTable.emptySolution.linkText"}}
            </LinkTo>
            {{#if @collection.isFavourite}}
              {{t "bookLibrary.components.collectionBooksTable.emptySolution.postAddToFavourites"}}
            {{else}}
              {{t "bookLibrary.components.collectionBooksTable.emptySolution.postAddToCollection"}}
            {{/if}}
          </p>
          <div class="border-dusty-black-100 mt-8 flex h-64 w-64 items-center justify-center border">
            <PhosphorIcon class="text-dusty-black-200 h-28 w-28" @icon="phosphor-image-fill" />
          </div>
        </div>
      {{else}}
        <BasicTable
          @config={{hash
            sortingConfig=this.sortingConfig
            onSortClick=this.updateSorts
            selectedIds=this.selectedIds
            allSelectableIds=this.selectableIds
            onSelectClick=this.updateSelected
          }}
          @columns={{this.columns}}
          @rows={{this.rows}}
          class="w-full print:table-auto"
          as |bt|
        >
          <bt.head as |h|>
            <h.row class="border-dusty-black-100 border-b" as |r cellMeta|>
              {{#unless cellMeta.columnObject.isHidden}}
                {{! there are multiple reading scale columns }}
                {{! only show the selected reading scale }}
                {{#if cellMeta.columnObject.isPrimaryFilterColumn}}
                  <r.cell class="w-48 font-normal">
                    <BookLibraryPrimaryFilterSelect
                      data-test-primary-filter-select
                      class="w-full px-3 py-1 uppercase"
                      @condensedList={{true}}
                      @primaryFilters={{@primaryFilters}}
                      @currentPrimaryFilter={{this.currentPrimaryFilter}}
                      @onPrimaryFilterChange={{this.updatePrimaryFilter}}
                    />
                  </r.cell>
                {{else if cellMeta.columnObject.isRemoveBooksColumn}}
                  <r.cell class="w-px px-2 py-1 text-center font-normal print:hidden">
                    <BooksTableBooksTaskButton
                      data-test-bulk-remove-books-button
                      @icon="phosphor-trash-fill"
                      @isDisabled={{this.disableBulkBookActions}}
                      @label={{t "bookLibrary.components.collectionBooksTable.removeSelectedBooks" htmlSafe=true}}
                      {{on "click" (fn this.removeBooks this.selectedBooks)}}
                    />
                  </r.cell>
                {{else if cellMeta.columnObject.isMoveBooksColumn}}
                  <r.cell class="w-px px-2 py-1 text-center font-normal print:hidden">
                    <PopoverStandardDropdown
                      @disabled={{this.disableBulkBookActions}}
                      @contentX="auto-right"
                      as |dropdown _status actions|
                    >
                      <BooksTableBooksTaskButton
                        data-test-bulk-move-books-button
                        @icon="phosphor-share-fat-fill"
                        @isDisabled={{this.disableBulkBookActions}}
                        @label={{t "bookLibrary.components.collectionBooksTable.moveBookToCollection" htmlSafe=true}}
                        {{dropdown.makeTrigger}}
                      />
                      <dropdown.content>
                        <BookLibraryCollectionSelect
                          @title={{t "bookLibrary.components.collectionBooksTable.moveBooks"}}
                          @collections={{this.otherCollections}}
                          @collectionsWithBook={{(array)}}
                          @onCreate={{fn this.moveBooks this.selectedBooks}}
                          @onSelect={{fn this.moveBooks this.selectedBooks}}
                          @closeAction={{actions.close}}
                        />
                      </dropdown.content>
                    </PopoverStandardDropdown>
                  </r.cell>
                {{else}}
                  <r.cell data-test-column-value-path={{cellMeta.columnObject.valuePath}} />
                {{/if}}
              {{/unless}}
            </h.row>
          </bt.head>
          <bt.body class={{if this.isTransforming "opacity-50"}} as |b rowMeta|>
            <b.row class="group" as |r cellMeta|>
              {{#if rowMeta.rowObject.bookModel.exclude}}
                <Tooltip @text={{t "bookLibrary.components.card.isExcluded"}} />
              {{/if}}
              {{#unless cellMeta.columnObject.isHidden}}
                {{#if cellMeta.columnObject.isRemoveBooksColumn}}
                  <r.cell class="text-center opacity-0 group-hover:opacity-100 print:hidden">
                    <BooksTableBooksTaskButton
                      data-test-remove-books-button
                      @icon="phosphor-trash-fill"
                      @isDisabled={{this.disableBookActions}}
                      @label={{t "bookLibrary.components.collectionBooksTable.removeBook" htmlSafe=true}}
                      {{on "click" (fn this.removeBooks (array rowMeta.rowObject.bookModel))}}
                    />
                  </r.cell>
                {{else if cellMeta.columnObject.isMoveBooksColumn}}
                  <r.cell class="text-center opacity-0 group-hover:opacity-100 print:hidden">
                    <PopoverStandardDropdown
                      @disabled={{this.disableBookActions}}
                      @contentX="auto-right"
                      as |dropdown _status actions|
                    >
                      <BooksTableBooksTaskButton
                        data-test-move-books-button
                        @icon="phosphor-share-fat-fill"
                        @isDisabled={{this.disableBookActions}}
                        @label={{t "bookLibrary.components.collectionBooksTable.moveBookToCollection" htmlSafe=true}}
                        {{dropdown.makeTrigger}}
                      />
                      <dropdown.content>
                        <BookLibraryCollectionSelect
                          @title={{t "bookLibrary.components.collectionBooksTable.moveBooks"}}
                          @collections={{this.otherCollections}}
                          @collectionsWithBook={{(array)}}
                          @onCreate={{fn this.moveBooks (array rowMeta.rowObject.bookModel)}}
                          @onSelect={{fn this.moveBooks (array rowMeta.rowObject.bookModel)}}
                          @closeAction={{actions.close}}
                        />
                      </dropdown.content>
                    </PopoverStandardDropdown>
                  </r.cell>
                {{else if cellMeta.columnObject.isResourcesColumn}}
                  <r.cell class="px-3">
                    {{#if rowMeta.rowObject.bookModel.bookMetadata.hasResources}}
                      <BookLibraryResourcesLink @bookId={{rowMeta.rowObject.bookModel.id}} />
                    {{/if}}
                  </r.cell>
                {{else}}
                  <r.cell />
                {{/if}}
              {{/unless}}
            </b.row>
          </bt.body>
        </BasicTable>
      {{/if}}
    </div>
    <BookLibraryConfirmBookChangesModal
      data-test-change-books-modal
      @collectionName={{@collection.name}}
      @onConfirm={{this.confirmChangeBooks}}
      @onCancel={{this.clearChangeBooks}}
      @isSubmitting={{or this.collectionService.moveBooks.isRunning this.collectionService.removeBooks.isRunning}}
    />
  </template>
}

export default BookLibraryCollectionBooksTable
